import * as THREE from 'three';
import { AIRBORNE } from '../utils/constants';
import type { VehicleState } from './vehicle';

function shortestAngleDiff(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/**
 * prd2 Day 5 — jump ramps: launch direction (rad), trigger box, visuals.
 */
export type JumpRamp = {
  id: string;
  x: number;
  z: number;
  /** Launch direction (radians, same as car heading: +Z forward at 0). */
  dirRad: number;
  halfWidth: number;
  depth: number;
  /** Ramp incline (deg) — used for mesh only; launch uses `launchVy`. */
  inclineDeg: number;
  /** Initial upward velocity (m/s). */
  launchVy: number;
  /** Multiply forward speed along ramp when launching. */
  speedMult: number;
  minSpeedMs: number;
};

/** Placements: Downtown (3), Docks (2), Highway mega (1), Industrial kickers (2). */
export const JUMP_RAMPS: JumpRamp[] = [
  {
    id: 'dt_1',
    x: -120,
    z: 280,
    dirRad: Math.PI * 0.92,
    halfWidth: 14,
    depth: 28,
    inclineDeg: 18,
    launchVy: 11,
    speedMult: 1.08,
    minSpeedMs: 9,
  },
  {
    id: 'dt_2',
    x: 220,
    z: 140,
    dirRad: -Math.PI * 0.35,
    halfWidth: 12,
    depth: 26,
    inclineDeg: 16,
    launchVy: 10,
    speedMult: 1.06,
    minSpeedMs: 8,
  },
  {
    id: 'dt_3',
    x: -40,
    z: -60,
    dirRad: Math.PI * 1.15,
    halfWidth: 13,
    depth: 24,
    inclineDeg: 17,
    launchVy: 10.5,
    speedMult: 1.07,
    minSpeedMs: 8,
  },
  {
    id: 'dock_1',
    x: -720,
    z: 620,
    dirRad: Math.PI * 0.5,
    halfWidth: 16,
    depth: 32,
    inclineDeg: 14,
    launchVy: 9,
    speedMult: 1.05,
    minSpeedMs: 7,
  },
  {
    id: 'dock_2',
    x: -580,
    z: 480,
    dirRad: Math.PI * 0.12,
    halfWidth: 15,
    depth: 30,
    inclineDeg: 15,
    launchVy: 9.5,
    speedMult: 1.05,
    minSpeedMs: 7,
  },
  {
    id: 'hw_mega',
    x: 680,
    z: 720,
    dirRad: -Math.PI * 0.22,
    halfWidth: 22,
    depth: 48,
    inclineDeg: 22,
    launchVy: 16,
    speedMult: 1.12,
    minSpeedMs: 14,
  },
  {
    id: 'ind_1',
    x: -180,
    z: -620,
    dirRad: Math.PI * 0.78,
    halfWidth: 11,
    depth: 22,
    inclineDeg: 20,
    launchVy: 12,
    speedMult: 1.06,
    minSpeedMs: 9,
  },
  {
    id: 'ind_2',
    x: 80,
    z: -780,
    dirRad: Math.PI * 1.05,
    halfWidth: 11,
    depth: 22,
    inclineDeg: 20,
    launchVy: 12,
    speedMult: 1.06,
    minSpeedMs: 9,
  },
  {
    id: 'blvd_1',
    x: -520,
    z: -340,
    dirRad: Math.PI * 0.65,
    halfWidth: 13,
    depth: 26,
    inclineDeg: 17,
    launchVy: 10,
    speedMult: 1.06,
    minSpeedMs: 8,
  },
  {
    id: 'hw_link',
    x: 420,
    z: 640,
    dirRad: -Math.PI * 0.42,
    halfWidth: 14,
    depth: 30,
    inclineDeg: 19,
    launchVy: 11,
    speedMult: 1.07,
    minSpeedMs: 9,
  },
];

const launchCooldownByRamp = new Map<string, number>();

function localRampCoords(
  wx: number,
  wz: number,
  ramp: JumpRamp,
): { along: number; lateral: number } {
  const c = Math.cos(ramp.dirRad);
  const s = Math.sin(ramp.dirRad);
  const dx = wx - ramp.x;
  const dz = wz - ramp.z;
  const along = dx * s + dz * c;
  const lateral = -dx * c + dz * s;
  return { along, lateral };
}

/**
 * If car enters ramp trigger with enough speed, sets airborne state. Call once per frame when grounded.
 */
export function tryLaunchFromRamps(
  s: VehicleState,
  dt: number,
): boolean {
  if (s.airborne) return false;

  const speed = Math.hypot(s.vx, s.vz);
  const sinH = Math.sin(s.heading);
  const cosH = Math.cos(s.heading);
  const fwd = s.vx * sinH + s.vz * cosH;
  if (fwd < 2) return false;

  for (const ramp of JUMP_RAMPS) {
    const cd = launchCooldownByRamp.get(ramp.id) ?? 0;
    if (cd > 0) {
      launchCooldownByRamp.set(ramp.id, Math.max(0, cd - dt));
      continue;
    }

    const { along, lateral } = localRampCoords(s.x, s.z, ramp);
    if (
      along >= -ramp.depth * 0.35 &&
      along <= ramp.depth * 0.95 &&
      Math.abs(lateral) <= ramp.halfWidth + 1.2 &&
      speed >= ramp.minSpeedMs
    ) {
      const rampFwdX = Math.sin(ramp.dirRad);
      const rampFwdZ = Math.cos(ramp.dirRad);
      const align = rampFwdX * sinH + rampFwdZ * cosH;
      if (align < 0.25) continue;

      s.airborne = true;
      s.airY = 0.02;
      s.vy = ramp.launchVy + speed * 0.12;
      s.launchHeading = s.heading;
      s.vx *= ramp.speedMult;
      s.vz *= ramp.speedMult;
      launchCooldownByRamp.set(ramp.id, 1.1);
      return true;
    }
  }
  return false;
}

export function stepAirborne(
  s: VehicleState,
  steer: number,
  dt: number,
): void {
  if (!s.airborne) return;
  s.vy -= AIRBORNE.GRAVITY * dt;
  s.airY += s.vy * dt;
  const airSteer = THREE.MathUtils.degToRad(AIRBORNE.STEER_DEG_PER_S) * steer;
  s.heading -= airSteer * dt;
  const sinH = Math.sin(s.heading);
  const cosH = Math.cos(s.heading);
  const speed = Math.hypot(s.vx, s.vz);
  s.vx = sinH * speed;
  s.vz = cosH * speed;
}

export type LandingResult = {
  impact: number;
  badLanding: boolean;
};

export function landVehicle(s: VehicleState): LandingResult {
  const impact = Math.abs(s.vy);
  s.airborne = false;
  s.airY = 0;
  s.vy = 0;
  const dh = Math.abs(shortestAngleDiff(s.heading, s.launchHeading));
  const badLanding = dh > THREE.MathUtils.degToRad(AIRBORNE.BAD_LANDING_ANGLE_DEG);
  if (badLanding) {
    s.vx *= AIRBORNE.BAD_LANDING_SPEED_RETAIN;
    s.vz *= AIRBORNE.BAD_LANDING_SPEED_RETAIN;
    s.yawRate += AIRBORNE.BAD_LANDING_YAW_KICK * (Math.random() > 0.5 ? 1 : -1);
  }
  return { impact, badLanding };
}

// ── Shared PBR materials (one instance each; no per-frame allocation) ──
const MAT = {
  deck: new THREE.MeshStandardMaterial({
    color: 0x4d525a,
    metalness: 0.52,
    roughness: 0.48,
    envMapIntensity: 0.55,
  }),
  deckGrip: new THREE.MeshStandardMaterial({
    color: 0x3a3e44,
    metalness: 0.35,
    roughness: 0.72,
    envMapIntensity: 0.35,
  }),
  frame: new THREE.MeshStandardMaterial({
    color: 0x3a3f48,
    metalness: 0.45,
    roughness: 0.55,
    envMapIntensity: 0.45,
  }),
  asphalt: new THREE.MeshStandardMaterial({
    color: 0x3a3a3e,
    metalness: 0.02,
    roughness: 0.92,
    envMapIntensity: 0.22,
  }),
  rail: new THREE.MeshStandardMaterial({
    color: 0xc8ccd4,
    metalness: 0.55,
    roughness: 0.38,
    envMapIntensity: 0.5,
  }),
  hazardYellow: new THREE.MeshStandardMaterial({
    color: 0xe8c820,
    metalness: 0.08,
    roughness: 0.55,
    emissive: 0x332200,
    emissiveIntensity: 0.06,
  }),
  hazardBlack: new THREE.MeshStandardMaterial({
    color: 0x1a1a1c,
    metalness: 0.15,
    roughness: 0.75,
  }),
  barrelOrange: new THREE.MeshStandardMaterial({
    color: 0xe85a18,
    metalness: 0.12,
    roughness: 0.55,
    emissive: 0x331000,
    emissiveIntensity: 0.08,
  }),
  barrelBand: new THREE.MeshStandardMaterial({
    color: 0xf5f0e8,
    metalness: 0.25,
    roughness: 0.45,
  }),
  rubber: new THREE.MeshStandardMaterial({
    color: 0x1c1c20,
    metalness: 0.02,
    roughness: 0.92,
  }),
};

/**
 * Portable stunt ramp: steel deck, I-beam runners, approach slab, side rails,
 * under-bracing, launch lip, traffic drums — no placeholder cone.
 */
function buildRealisticRampVisual(ramp: JumpRamp): THREE.Group {
  const group = new THREE.Group();
  const incline = THREE.MathUtils.degToRad(ramp.inclineDeg);
  const d = ramp.depth;
  const hw = ramp.halfWidth;
  const deckThick = 0.14;

  // Same vertical placement as legacy wedge so the driving plane matches triggers
  const deckCenterY =
    deckThick * 0.5 * Math.cos(incline) + d * 0.22 * Math.sin(incline);

  // ── Approach: flush asphalt run-up (car rolls on before the kicker) ──
  const approachLen = d * 0.38;
  const approach = new THREE.Mesh(
    new THREE.BoxGeometry(hw * 2.15, 0.07, approachLen),
    MAT.asphalt,
  );
  approach.position.set(0, 0.035, -d * 0.5 - approachLen * 0.5 + d * 0.04);
  approach.receiveShadow = true;
  approach.castShadow = true;
  group.add(approach);

  // Faded lane edge lines on approach (thin dark strips)
  for (const sx of [-hw * 0.92, hw * 0.92]) {
    const line = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.01, approachLen * 0.85),
      MAT.hazardBlack,
    );
    line.position.set(sx, 0.075, approach.position.z);
    line.receiveShadow = true;
    group.add(line);
  }

  // ── Main steel deck (inclined) ──
  const deck = new THREE.Mesh(
    new THREE.BoxGeometry(hw * 2, deckThick, d),
    MAT.deck,
  );
  deck.rotation.x = -incline;
  deck.position.set(0, deckCenterY, 0);
  deck.castShadow = true;
  deck.receiveShadow = true;
  group.add(deck);

  // Anti-slip tread strips on deck (dark bands across width)
  const stripCount = Math.max(3, Math.floor(d / 5));
  for (let i = 0; i < stripCount; i++) {
    const tz = -d * 0.42 + (i / (stripCount - 1)) * d * 0.78;
    const strip = new THREE.Mesh(
      new THREE.BoxGeometry(hw * 1.92, 0.025, 0.35),
      MAT.deckGrip,
    );
    strip.rotation.x = -incline;
    strip.position.set(
      0,
      deckCenterY + tz * Math.sin(incline) + 0.08 * Math.cos(incline),
      tz,
    );
    strip.castShadow = false;
    strip.receiveShadow = true;
    group.add(strip);
  }

  // Side I-beam runners (full length, under deck edges)
  const beamH = 0.28;
  const beamW = 0.12;
  for (const sx of [-hw - 0.06, hw + 0.06]) {
    const beam = new THREE.Mesh(
      new THREE.BoxGeometry(beamW, beamH, d + 0.4),
      MAT.frame,
    );
    beam.rotation.x = -incline;
    beam.position.set(
      sx,
      deckCenterY - 0.12 * Math.cos(incline) - beamH * 0.35,
      0,
    );
    beam.castShadow = true;
    beam.receiveShadow = true;
    group.add(beam);
  }

  // Cross-members under deck (every ~1/3 length)
  for (let ci = 0; ci < 3; ci++) {
    const cz = -d * 0.35 + ci * d * 0.35;
    const cross = new THREE.Mesh(
      new THREE.BoxGeometry(hw * 2.05, 0.08, 0.14),
      MAT.frame,
    );
    cross.rotation.x = -incline;
    cross.position.set(
      0,
      deckCenterY - 0.2 * Math.cos(incline) + cz * Math.sin(incline) * 0.02,
      cz,
    );
    cross.castShadow = true;
    group.add(cross);
  }

  // Diagonal brace (one visible X under center)
  const braceLen = d * 0.55;
  const brace = new THREE.Mesh(
    new THREE.CylinderGeometry(0.07, 0.07, braceLen, 6),
    MAT.frame,
  );
  brace.rotation.z = Math.PI / 2;
  brace.rotation.y = 0.35;
  brace.position.set(-hw * 0.5, deckCenterY * 0.35, 0);
  brace.castShadow = true;
  group.add(brace);
  const brace2 = brace.clone();
  brace2.rotation.y = -0.35;
  brace2.position.x = hw * 0.5;
  group.add(brace2);

  // ── Guard rails (posts + top rail) ──
  const railH = 0.95;
  const postZ = [-d * 0.4, -d * 0.1, d * 0.15, d * 0.42];
  for (const sx of [-hw - 0.04, hw + 0.04]) {
    for (const pz of postZ) {
      const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.06, railH, 0.06),
        MAT.rail,
      );
      const baseY =
        deckCenterY + pz * Math.sin(incline) + deckThick * 0.5 * Math.cos(incline);
      post.position.set(sx, baseY + railH * 0.45, pz);
      post.rotation.x = -incline * 0.15;
      post.castShadow = true;
      group.add(post);
    }
    const topRail = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.06, d * 0.95),
      MAT.rail,
    );
    topRail.rotation.x = -incline;
    topRail.position.set(
      sx,
      deckCenterY + d * 0.35 * Math.sin(incline) + 0.55 * Math.cos(incline),
      0,
    );
    topRail.castShadow = true;
    group.add(topRail);
  }

  // ── Launch lip: rubberized edge + hazard stripes at sides of lip ──
  const lipZ = d * 0.48;
  const lipY =
    deckCenterY + lipZ * Math.sin(incline) + deckThick * 0.5 * Math.cos(incline) + 0.04;
  const lip = new THREE.Mesh(
    new THREE.BoxGeometry(hw * 2.02, 0.06, 0.22),
    MAT.rubber,
  );
  lip.rotation.x = -incline;
  lip.position.set(0, lipY, lipZ);
  lip.castShadow = true;
  group.add(lip);

  for (let i = -2; i <= 2; i++) {
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(0.45, 0.04, 0.12),
      i % 2 === 0 ? MAT.hazardYellow : MAT.hazardBlack,
    );
    stripe.rotation.x = -incline;
    stripe.position.set(i * 0.55, lipY + 0.04, lipZ + 0.02);
    group.add(stripe);
  }

  // ── Traffic drums (weighted barriers, not a floating cone) ──
  function addBarrel(bx: number, bz: number, scale = 1): void {
    const body = new THREE.Mesh(
      new THREE.CylinderGeometry(0.42 * scale, 0.48 * scale, 0.95 * scale, 10),
      MAT.barrelOrange,
    );
    body.position.set(bx, 0.48 * scale, bz);
    body.castShadow = true;
    group.add(body);
    const band = new THREE.Mesh(
      new THREE.TorusGeometry(0.44 * scale, 0.035, 6, 12),
      MAT.barrelBand,
    );
    band.rotation.x = Math.PI / 2;
    band.position.set(bx, 0.55 * scale, bz);
    group.add(band);
    const top = new THREE.Mesh(
      new THREE.CylinderGeometry(0.38 * scale, 0.38 * scale, 0.06 * scale, 8),
      MAT.barrelOrange,
    );
    top.position.set(bx, 0.94 * scale, bz);
    group.add(top);
  }

  const drumZ = -d * 0.52 - approachLen * 0.35;
  addBarrel(-hw - 1.1, drumZ, 1);
  addBarrel(hw + 1.1, drumZ, 1);
  addBarrel(-hw - 0.9, drumZ + 2.5, 0.85);
  addBarrel(hw + 0.9, drumZ + 2.5, 0.85);

  return group;
}

/** Realistic portable stunt ramps — steel deck, rails, drums, approach slab. */
export function addJumpRampMeshes(root: THREE.Group): void {
  for (const ramp of JUMP_RAMPS) {
    const group = buildRealisticRampVisual(ramp);
    group.position.set(ramp.x, 0, ramp.z);
    group.rotation.y = ramp.dirRad;
    root.add(group);
  }
}

export function getRampMinimapPoints(): { x: number; z: number }[] {
  return JUMP_RAMPS.map((r) => ({ x: r.x, z: r.z }));
}
